import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { CheckIcon, CloseIcon, InfoIcon, WarningIcon } from '@components/icons';

/**
 * Toast notification - custom replacement for native alert().
 * Auto-dismisses after duration, supports multiple variants.
 */
export const Toast = observer(function Toast() {
  const toasts = bibleState.toasts;

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
});

interface ToastItemProps {
  toast: {
    id: string;
    message: string;
    variant?: 'info' | 'success' | 'warning' | 'error';
    duration?: number;
  };
}

function ToastItem({ toast }: ToastItemProps) {
  const [animationState, setAnimationState] = useState<'entering' | 'visible' | 'exiting'>(
    'entering',
  );

  useEffect(() => {
    // Enter animation
    const enterTimer = setTimeout(() => setAnimationState('visible'), 50);

    // Auto-dismiss
    const duration = toast.duration ?? 3000;
    const dismissTimer = setTimeout(() => {
      setAnimationState('exiting');
      setTimeout(() => {
        bibleState.removeToast(toast.id);
      }, 200);
    }, duration);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(dismissTimer);
    };
  }, [toast.id, toast.duration]);

  const handleDismiss = () => {
    setAnimationState('exiting');
    setTimeout(() => {
      bibleState.removeToast(toast.id);
    }, 200);
  };

  const Icon = {
    info: InfoIcon,
    success: CheckIcon,
    warning: WarningIcon,
    error: CloseIcon,
  }[toast.variant || 'info'];

  const variantClasses = {
    info: 'bg-cyan-50 dark:bg-cyan-900/80 border-cyan-200 dark:border-cyan-700/50 text-cyan-800 dark:text-cyan-200',
    success:
      'bg-green-50 dark:bg-green-900/80 border-green-200 dark:border-green-700/50 text-green-800 dark:text-green-200',
    warning:
      'bg-amber-50 dark:bg-amber-900/80 border-amber-200 dark:border-amber-700/50 text-amber-800 dark:text-amber-200',
    error:
      'bg-red-50 dark:bg-red-900/80 border-red-200 dark:border-red-700/50 text-red-800 dark:text-red-200',
  }[toast.variant || 'info'];

  const iconClasses = {
    info: 'text-cyan-500',
    success: 'text-green-500',
    warning: 'text-amber-500',
    error: 'text-red-500',
  }[toast.variant || 'info'];

  return (
    <div
      className={`toast-item ${variantClasses} ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'exiting' : ''}`}
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${iconClasses}`} />
      <span className="flex-1">{toast.message}</span>
      <button
        onClick={handleDismiss}
        className="rounded-full p-1 transition-colors hover:bg-black/10 dark:hover:bg-white/10"
      >
        <CloseIcon className="h-4 w-4 opacity-60" />
      </button>
    </div>
  );
}

export default Toast;
